import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useSegments from 'api/useSegments';

import Badge from 'components/Badge/Badge';
import Input from 'components/Input/Input';
import RichText from 'components/RichText/RichText';

interface SegmentInputProps {
  value: string[];
  onChange: (segments: string[]) => void;
  label?: string;
  description?: string;
  placeholder?: string;
  dataTestId?: string;
  className?: string;
  withRichText?: boolean;
  richTextTitle?: string;
  richTextDescription?: string;
}

const SegmentInput: FC<SegmentInputProps> = ({
  value = [],
  onChange,
  label = 'Segment Name',
  description,
  placeholder = 'Search Segment',
  dataTestId = '',
  className = '',
  withRichText = true,
  richTextTitle = 'Segments',
  richTextDescription = 'Create or add players segments to your offer'
}) => {
  const { publisherId } = useParams();
  const { getSegments } = useSegments(publisherId);

  const [inputValue, setInputValue] = useState('');
  const [segmentError, setSegmentError] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    const apiSegments: string[] = getSegments.data || [];
    if (!inputValue.trim()) return [];
    return apiSegments.filter(
      (s) =>
        s.toLowerCase().includes(inputValue.trim().toLowerCase()) &&
        !value.some((v) => v.toLowerCase() === s.toLowerCase())
    );
  }, [getSegments.data, inputValue, value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddSegment = (segment: string) => {
    const trimmed = segment.trim();
    if (!trimmed) return;

    if (value.some((s) => s.toLowerCase() === trimmed.toLowerCase())) {
      setSegmentError("You can't add the same segment twice");
      return;
    }

    onChange([...value, trimmed]);
    setInputValue('');
    setSegmentError('');
    setIsPopoverOpen(false);
  };

  const handleRemoveSegment = (segmentToRemove: string) => {
    onChange(value.filter((s) => s !== segmentToRemove));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInputValue(v);
    setIsPopoverOpen(v.trim().length > 0);
    setSegmentError(
      value.some((s) => s.toLowerCase() === v.trim().toLowerCase())
        ? "You can't add the same segment twice"
        : ''
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      handleAddSegment(inputValue);
    }
  };

  return (
    <div className={className} ref={wrapperRef}>
      {withRichText && (
        <RichText
          title={richTextTitle}
          description={richTextDescription}
          className="mb-5"
        />
      )}
      <div className="relative">
        <Input
          ref={inputRef}
          label={label}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.trim()) setIsPopoverOpen(true);
          }}
          onBlur={() => setSegmentError('')}
          state={segmentError ? 'error' : 'default'}
          description={
            segmentError ||
            description ||
            (value.length === 0 ? "Press 'Enter' to add a new segment" : '')
          }
          dataTestId={dataTestId}
          rightIcon={
            <FontAwesomeIcon
              icon="fa-regular fa-magnifying-glass"
              className="w-4 h-4"
            />
          }
        />
        {isPopoverOpen && filteredOptions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-base-input rounded-md shadow-md max-h-48 overflow-auto">
            {filteredOptions.map((option) => (
              <button
                key={option}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-base-accent cursor-pointer"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleAddSegment(option);
                }}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
      {value.length > 0 && (
        <div className="flex flex-row flex-wrap gap-2 mt-3">
          {value.map((segment) => (
            <Badge
              key={segment}
              isRemovable
              onRemove={() => handleRemoveSegment(segment)}
            >
              {segment}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

SegmentInput.displayName = 'SegmentInput';

export default SegmentInput;
